import * as React from 'react';

import { enumAddressType } from '@rematter/frontend-lib-api-client';
import {
  AsyncBoundary,
  Button,
  Col,
  PhoneField,
  Row,
  Select,
  SelectCountry,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideBuilding } from 'lucide-react';

import {
  $locationForm,
  $updateLocationForm,
} from '../../_state/locationForm.state';
import {
  $currentLocation,
  $updateFacility,
} from '../../_state/locations.state';
import { FormSection } from '../_shared/FormSection';
import { SettingsContent } from '../_shared/SettingsContent';

import { SelectFacilityGroup } from '~/shared/components/select-fields/SelectFacilityGroup';
import { states as usStates } from '~/shared/enumerables/usStates';

export const LocationInfo = () => {
  const toast = useToast();
  const currentLocation = useAtomValue($currentLocation);
  const form = useAtomValue($locationForm);
  const updateForm = useSetAtom($updateLocationForm);

  const updateFacility = useSetAtom($updateFacility);

  // Initialize form from currentLocation
  React.useEffect(() => {
    if (currentLocation) {
      updateForm({
        facilityName: currentLocation.name || '',
        facilityGroupId: currentLocation.facilityGroup?.id,
        facilityType: currentLocation.type || '',
        timezone:
          currentLocation.timezone ||
          Intl.DateTimeFormat().resolvedOptions().timeZone,
        address: {
          streetAddress: currentLocation.address?.streetAddress || '',
          // addressLine2:
          //   (currentLocation.address?.addressLine2 as string | undefined) ||
          //   undefined,
          country: currentLocation.address?.country || '',
          postalCode: currentLocation.address?.postalCode || '',
          city: currentLocation.address?.city || '',
          state: currentLocation.address?.state || '',
        },
        // phone: (currentLocation.phone as string | undefined) || undefined,
        // email: (currentLocation.email as string | undefined) || undefined,
      });
    }
  }, [currentLocation, updateForm]);

  const handleFieldChange =
    <K extends keyof typeof form>(field: K) =>
    (value: unknown) => {
      updateForm({
        [field]: value as (typeof form)[K],
      });
    };

  const handleAddressFieldChange =
    (field: keyof typeof form.address) => (value: unknown) => {
      updateForm({
        address: {
          ...form.address,
          [field]: value as string,
        },
      });
    };

  const handleDiscard = () => {
    if (currentLocation) {
      updateForm({
        facilityName: currentLocation.name || '',
        facilityGroupId: currentLocation.facilityGroup?.id,
        facilityType: currentLocation.type || '',
        timezone:
          currentLocation.timezone ||
          Intl.DateTimeFormat().resolvedOptions().timeZone,
        address: {
          streetAddress: currentLocation.address?.streetAddress || '',
          // addressLine2:
          //   (currentLocation.address?.addressLine2 as string | undefined) ||
          //   undefined,
          addressLine2: undefined,
          country: currentLocation.address?.country || '',
          postalCode: currentLocation.address?.postalCode || '',
          city: currentLocation.address?.city || '',
          state: currentLocation.address?.state || '',
        },
        // phone: (currentLocation.phone as string | undefined) || undefined,
        // email: (currentLocation.email as string | undefined) || undefined,
        phone: undefined,
        email: undefined,
      });
    }
  };

  const handleSave = async () => {
    if (!currentLocation?.id) return;

    try {
      await updateFacility({
        id: currentLocation.id,
        facilityName: form.facilityName,
        facilityType: form.facilityType,
        facilityGroupId: form.facilityGroupId,
        timezone: form.timezone,
        address: {
          streetAddress: form.address.streetAddress,
          city: form.address.city,
          state: form.address.state,
          country: form.address.country,
          postalCode: form.address.postalCode,
          addressType: enumAddressType.Standard,
          isMailingAddress: true,
          ...(form.address.addressLine2
            ? { streetAddress2: form.address.addressLine2 }
            : {}),
        } as never,
        phone: form.phone,
        email: form.email,
      });
      toast.success('Location updated successfully');
    } catch (error) {
      console.error('Failed to update facility:', error);
      toast.error('Failed to update location');
    }
  };

  const hasChanges = React.useMemo(() => {
    if (!currentLocation) return false;

    return (
      form.facilityName !== (currentLocation.name || '') ||
      form.facilityGroupId !== currentLocation.facilityGroup?.id ||
      form.facilityType !== (currentLocation.type || '') ||
      form.timezone !== (currentLocation.timezone || '') ||
      form.address.streetAddress !==
        (currentLocation.address?.streetAddress || '') ||
      form.address.country !== (currentLocation.address?.country || '') ||
      form.address.postalCode !== (currentLocation.address?.postalCode || '') ||
      form.address.city !== (currentLocation.address?.city || '') ||
      form.address.state !== (currentLocation.address?.state || '') ||
      (form.address.addressLine2 || '') !== '' ||
      (form.phone || '') !== '' ||
      (form.email || '') !== ''
    );
  }, [form, currentLocation]);

  const timezoneOptions = React.useMemo(
    () =>
      Intl.supportedValuesOf('timeZone').map((tz) => ({
        key: tz,
        label: tz.replaceAll('_', ' '),
        searchable: tz,
      })),
    [],
  );

  const usStateOptions = React.useMemo(
    () =>
      usStates.map((it) => ({
        key: it.abbreviation,
        label: it.name,
        searchable: `${it.name} ${it.abbreviation}`,
      })),
    [],
  );

  return (
    <AsyncBoundary
      loading={!currentLocation}
      className="@LocationInfo"
      error={null}
    >
      <Col className={'@LocationInfo h-full'}>
        <Col gap6 className={'h-0 grow overflow-y-auto'}>
          <SettingsContent size="medium" className="gap-6 p-5">
            {/* Facility Logo Section */}
            {/* <Row gap3>
              <Row
                xCenter
                yCenter
                className={
                  'aspect-square h-full shrink-0 rounded-lg bg-primary/10'
                }
              >
                LOGO
              </Row>
              <Col gap3>
                <Col>
                  <div className="text-sm font-semibold">Facility Logo</div>
                  <div className="text-sm text-neutral/60">
                    Upload PNG or JPEG under 5MB
                  </div>
                </Col>
                <Row gap2>
                  <Button
                    variant="outline"
                    color="primary"
                    icon={<LucideUpload className="size-4" />}
                    isDisabled
                  >
                    Update logo
                  </Button>
                  <Button
                    variant="outline"
                    color="failure"
                    icon={<LucideTrash2 className="size-4" />}
                    isDisabled
                  />
                </Row>
              </Col>
            </Row> */}

            {/* Facility Info Section */}
            <FormSection icon={LucideBuilding} title="Facility Info">
              <Col gap4>
                <TextField
                  label="Location Name"
                  value={form.facilityName}
                  onChange={handleFieldChange('facilityName')}
                  isRequired
                />

                <div className="grid grid-cols-2 gap-4">
                  <SelectFacilityGroup
                    label="Facility Group"
                    value={form.facilityGroupId}
                    emptyOptionLabel="No group"
                    withEmptyOption
                    onChange={(value) =>
                      handleFieldChange('facilityGroupId')(value || null)
                    }
                    placeholder="Select..."
                  />

                  <TextField
                    label="Facility Type"
                    value={form.facilityType}
                    onChange={handleFieldChange('facilityType')}
                    isRequired
                  />
                </div>

                <Select
                  label="Timezone"
                  value={form.timezone}
                  onChange={handleFieldChange('timezone')}
                  options={timezoneOptions}
                  isRequired
                />

                <div className="grid grid-cols-1 gap-4">
                  <TextField
                    label="Address #1"
                    value={form.address.streetAddress}
                    onChange={handleAddressFieldChange('streetAddress')}
                    isRequired
                  />

                  <TextField
                    label="Address #2"
                    value={form.address.addressLine2 || ''}
                    onChange={handleAddressFieldChange('addressLine2')}
                    placeholder="Text Attribute Name"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <SelectCountry
                    label="Country"
                    value={form.address.country}
                    onSelectionChange={(value) =>
                      handleAddressFieldChange('country')(value || '')
                    }
                    isRequired
                  />

                  <TextField
                    label="ZIP Code"
                    value={form.address.postalCode}
                    onChange={handleAddressFieldChange('postalCode')}
                    isRequired
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <TextField
                    label="City"
                    value={form.address.city}
                    onChange={handleAddressFieldChange('city')}
                    isRequired
                  />

                  {form.address.country === 'US' ? (
                    <Select
                      label="State"
                      value={form.address.state}
                      onChange={handleAddressFieldChange('state')}
                      options={usStateOptions}
                      placeholder="Select..."
                      isRequired
                    />
                  ) : (
                    <TextField
                      label="State"
                      value={form.address.state}
                      onChange={handleAddressFieldChange('state')}
                      isRequired
                    />
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <PhoneField
                    label="Location Phone"
                    value={form.phone || ''}
                    onChange={(value) =>
                      handleFieldChange('phone')(value || undefined)
                    }
                  />

                  <TextField
                    label="Location Email"
                    value={form.email || ''}
                    onChange={(value) =>
                      handleFieldChange('email')(value || undefined)
                    }
                    placeholder="example@email.com"
                    type="email"
                  />
                </div>
              </Col>
            </FormSection>
          </SettingsContent>
        </Col>
        {/* Action Buttons */}
        <SettingsContent size="medium" className={'border-t border-neutral/15'}>
          <Row xEnd gap2 className={'p-5'}>
            <Button
              variant="outline"
              onPress={handleDiscard}
              isDisabled={!hasChanges}
            >
              Discard
            </Button>
            <Button
              variant="solid"
              color="primary"
              onPress={handleSave}
              isDisabled={!hasChanges}
            >
              Save Changes
            </Button>
          </Row>
        </SettingsContent>
      </Col>
    </AsyncBoundary>
  );
};
